import { DashboardFederationAvailable, GeographicScope } from "@/api/__generated__/schemas";
import { mapToAvailableUserRef } from "@/mappers/availablePlatform.mappers";
import { mapToCapabilitySet } from "@/mappers/capability.mappers";
import { mapToOrganizationType } from "@/mappers/organization.mappers";
import { AvailableFederation } from "@/types/availableFederation.types";
import { CountryCode } from "@/types/common.types";
import { OrganizationGeoScope } from "@/types/organization.types";

/**
 * REST `DashboardFederationAvailable` → FE `AvailableFederation`.
 *
 * `countries[]` arrives pre-parsed from BE; no `JSON.parse`. `mainManager` is a slim
 * `UserRef` — no email / phone.
 */
export const mapToAvailableFederation = (
  input: DashboardFederationAvailable
): AvailableFederation => ({
  id: input.id,
  name: input.organizationName ?? "",
  abbreviation: input.organizationAbbreviation ?? null,
  logo: input.logo ?? null,
  // TODO(available-federation-logo): the REST DashboardFederationAvailable DTO does not
  // yet expose the storage-backed logo. Once the backend adds `logoFile` (StorageFile) +
  // `legacyLogoUrl` — mirroring DashboardUserSummary.avatarFile + legacyAvatarUrl —
  // re-run `yarn rest:codegen` and map `logoFile` from `input.logoFile` and
  // `legacyLogoUrl` from `input.legacyLogoUrl`. Until then the legacy `logo` column is
  // the only source: surface it as `legacyLogoUrl` and leave `logoFile` null so the
  // OrganizationLogo storage-backed fallback still resolves to the legacy URL.
  logoFile: null,
  legacyLogoUrl: input.logo ?? null,
  organizationTypes: input.organizationTypes.map(mapToOrganizationType),
  countries: (input.countries ?? []).filter((code): code is CountryCode => Boolean(code)),
  geographicScope: input.geographicScope ? mapGeographicScope(input.geographicScope) : null,
  enableLicenses: input.enableLicenses,
  enableCertificates: input.enableCertificates,
  enableApplicationForms: input.enableApplicationForms,
  enableRankings: input.enableRankings,
  isClubForTeamRequired: input.isClubForTeamRequired ?? false,
  // Interim, pre-RBAC per-caller gate. Default to `true` (show) when the BE hasn't
  // shipped the field yet: the transaction list/stats/export are already scoped
  // server-side (getExcludedTransactionFederationIds), so an old-BE response that
  // omits this flag must keep the current behavior — not hide the surface for
  // everyone during the deploy gap. Real `false` from the BE still hides it.
  canViewTransactions: input.canViewTransactions ?? true,
  mainManager: input.mainManager ? mapToAvailableUserRef(input.mainManager) : null,
  // Defensive: `mapToCapabilitySet` tolerates a BE that has not shipped the
  // field yet (deploy gap) by resolving to an empty set — under strict RBAC an
  // empty set denies access (fail closed) until the field ships.
  capabilities: mapToCapabilitySet(input.capabilities),
  // `plt_…` public ids of the platforms the federation operates on. `?? []`
  // covers the deploy gap (BE without the field) — the pair-attachment guard
  // treats an empty list as "unknown" and fails open.
  attachedPlatformIds: input.platformPublicIds ?? [],
});

const mapGeographicScope = (scope: GeographicScope): OrganizationGeoScope => {
  switch (scope) {
    case "international":
      return "international";
    case "national":
      return "national";
    case "local":
      return "local";
  }
};
